/**
 * useClientProgress Hook
 *
 * Fetches all progress data for a client in parallel for the coach view.
 * Includes SAT scores, practice plan, recommendations, score history, and activity heatmap.
 */

import { useState, useEffect, useCallback } from 'react'
import api from '../services/api'

import { createLogger } from '../utils/logger'

const logger = createLogger('useClientProgress')

/**
 * Hook to fetch comprehensive progress data for a coach's client
 * @param {string} clientId - The client's user ID
 * @param {boolean} hasPermission - Whether the coach has canViewProgress permission (default: true)
 * @returns {Object} Progress data, loading state, error, and refresh function
 */
export function useClientProgress(clientId, hasPermission = true) {
  const [data, setData] = useState({
    perSubjectScores: null,
    practiceRecommendation: null,
    recommendations: null,
    mathActualHistory: null,
    rwActualHistory: null,
    activityHeatmap: null,
    goal: null
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    if (!clientId || !hasPermission) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch all data in parallel
      const [
        perSubjectResponse,
        practiceResponse,
        recommendationsResponse,
        mathHistoryResponse,
        rwHistoryResponse,
        activityResponse,
        goalResponse
      ] = await Promise.allSettled([
        api.get(`/predictions/user/${clientId}/current`),
        api.get(`/predictions/user/${clientId}/practice-recommendation`),
        api.get(`/predictions/user/${clientId}/recommendations`),
        api.get(`/predictions/user/${clientId}/actual-history/SAT_MATH`),
        api.get(`/predictions/user/${clientId}/actual-history/SAT_READING_WRITING`),
        api.get(`/user/${clientId}/activity-heatmap`),
        api.get(`/predictions/user/${clientId}/goal`)
      ])

      setData({
        perSubjectScores:
          perSubjectResponse.status === 'fulfilled' ? perSubjectResponse.value.data.data : null,
        practiceRecommendation:
          practiceResponse.status === 'fulfilled' ? practiceResponse.value.data.data : null,
        recommendations:
          recommendationsResponse.status === 'fulfilled'
            ? recommendationsResponse.value.data.data
            : null,
        mathActualHistory:
          mathHistoryResponse.status === 'fulfilled' ? mathHistoryResponse.value.data.data : null,
        rwActualHistory:
          rwHistoryResponse.status === 'fulfilled' ? rwHistoryResponse.value.data.data : null,
        activityHeatmap:
          activityResponse.status === 'fulfilled' ? activityResponse.value.data.data : null,
        goal: goalResponse.status === 'fulfilled' ? goalResponse.value.data.data : null
      })
    } catch (err) {
      logger.error('Error fetching client progress:', err)
      setError(err.message || 'Failed to load progress data')
    } finally {
      setLoading(false)
    }
  }, [clientId, hasPermission])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    loading,
    error,
    refresh: fetchData
  }
}

export default useClientProgress
